import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import {
  Grid,
  LinearProgress,
  Popover,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { convertDurationToSeconds, hrsToMnsToSecToMlSec } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import AddIcon from '@mui/icons-material/Add';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { Button, Modal, Typography } from 'components';
import UndoSharpIcon from '@mui/icons-material/UndoSharp';
import RedoSharpIcon from '@mui/icons-material/RedoSharp';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  addActivity,
  addClip,
  applyToAllVideos,
  deleteAllClips,
  deleteClip
} from 'store/config/activities';
import { useDispatch, useSelector } from 'react-redux';
import applyToAll from '../../../assets/images/applyToAll.svg';
import replicate from '../../../assets/images/replicate.svg';
import { RootState } from 'store';
import { ConfigCameras } from 'store/config/schema';
import { useSnack } from 'plugins/snack';
import { useAutoLabel } from 'api/sdk';
import { useActivityConfiguration } from 'hooks/config/activityConfig';

function TimelinePlayer({
  timelineState,
  videoRef,
  setLabelLoader,
  currentConfigId,
  isCurrentVideoEnabled,
  showText,
  loadingVideo,
  onClickAdd,
  isPlaying,
  onClickCrop,
  scale,
  disableCrop,
  videoPath,
  editorData,
  activity,
  currentCamera,
  getCropState,
  activityData,
  currentCameraId,
  setActivityDataPointer,
  setIsPlaying,
  activityDataPointer,
  setZoomCOunt,
  allVideo,
  zoomCount
}: {
  timelineState: React.MutableRefObject<TimelineState | null>;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  maxEndtime: number;
  activity: any;
  scale: number;
  editorData?: any;
  setLabelLoader?: any;
  loadingVideo: boolean;
  videoPath?: any;
  setSelectedActivity?: string;
  selectedActivity?: any;
  disableCrop: boolean;
  currentCamera?: ConfigCameras;
  currentCameraId?: string;
  showText: boolean;
  onClickAdd: () => void;
  currentConfigId?: string | undefined;
  setZoomCOunt: React.Dispatch<React.SetStateAction<number>>;
  zoomCount: number;
  allVideo?: any;
  time: number;
  activityData: any;
  activityDataPointer: number;
  setActivityDataPointer: React.Dispatch<React.SetStateAction<number>>;
  onClickCrop: () => void;
  handlePlay: () => void;
  handlePause: () => void;
  isPlaying: boolean;
  setActivityData: any;
  isCurrentVideoEnabled?: boolean;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  getCropState: any;
}) {
  const dispatch = useDispatch();
  const snack = useSnack();
  const { mutateAsync, isLoading } = useAutoLabel();
  const [openMessage, setOpenmessage] = useState(false);
  const { updateList } = useActivityConfiguration();

  // Memoized selectors
  const videoData = useSelector(
    (data: any) => data?.configuration?.filteredVideo
  );
  const allVideos = useSelector((state: any) => state?.configV2.cameras);
  const allactivities = useSelector(
    (state: RootState) => state.activities.data
  );
  const replicated = useSelector((state: any) => state?.config?.replicate);
  const labelAssisData = useSelector(
    (state: any) => state?.config?.labelAssistData
  );

  // State hooks
  const [isOpen, setOpen] = useState({ open: false, value: '' });
  const [openPop, setPopOpen] = useState(false);
  const [labelPop, setLabelPop] = useState(false);
  const [selectedLableSource, setSelectedLableSource] = useState<string>();
  const [selectedItems, setSelectedItems] = useState<any>([]);
  const [sourceCameraId, setSourceCameraId] = useState<string | undefined>('');
  const [sourceDuration, setSourceDuration] = useState();

  // Memoized transformations
  const transformActivities = useMemo(() => {
    const cameraGroupedClips = allactivities.reduce(
      (acc: any, activity: any) => {
        activity.clips.forEach((clip: any) => {
          if (!acc[clip.camera_id]) {
            acc[clip.camera_id] = {
              cameraId: clip.camera_id,
              clips: [],
              videoUrl: ''
            };
          }

          acc[clip.camera_id].clips.push({
            ...clip,
            activity: activity.activity
          });
        });

        return acc;
      },
      {}
    );

    return Object.values(cameraGroupedClips);
  }, [allactivities]);
  const assistSourceData = useMemo(() => {
    return activity
      .map((data: any) => ({
        ...data,
        clips: data.clips.filter(
          (clip: any) => clip.camera_id === selectedLableSource
        )
      }))
      .filter((activity: any) => activity.clips.length > 0);
  }, [selectedLableSource, activity]);

  // Memoized callbacks
  const handleCheckboxChange = useCallback(
    (event: any, option: any) => {
      if (event.target.checked) {
        setSelectedItems((prevSelected: any) => [...prevSelected, option]);
      } else {
        setSelectedItems((prevSelected: any) =>
          prevSelected.filter((item: any) => item !== option)
        );
      }
    },
    [allVideos, selectedItems]
  );

  const onSourceChange = useCallback(
    (data: any) => {
      const videoDuration = allVideos.filter(
        (vData: any) => vData?.id === data?.cameraId
      )[0].video_metadata.duration;
      setSourceCameraId(data?.cameraId);
      setSourceDuration(videoDuration);
      setSelectedItems([]);
    },
    [allVideos, selectedItems]
  );

  const checkAllActivities = useCallback(
    (value: string) => {
      let count = 0;
      allactivities.forEach((data: any) => {
        if (data?.clips.length) {
          count += 1;
        }
      });
      if (count === 0) {
        setOpen({ open: true, value: value });
      } else if (count !== 0 && value === 'Apply to All') {
        setPopOpen(true);
        setOpenmessage(true);
      } else {
        if (value === 'Label Assist') {
          transformActivities.filter(
            (filteredData: any) =>
              filteredData?.cameraId !== currentCameraId &&
              videoData?.some((video: any) => video === filteredData?.cameraId)
          ).length && setLabelPop(true);
        }
      }
    },
    [allactivities, currentCameraId, videoData, transformActivities]
  );

  const callLabelAssist = useCallback(async () => {
    await mutateAsync({
      params: { config_id: currentConfigId || '' },
      data: {
        config_id: currentConfigId || '',
        source_annotation: assistSourceData,
        source_camera_id: selectedLableSource || '',
        source_video_path: allVideos.filter(
          (data: any) => data.id === selectedLableSource
        )[0]?.video_path,
        target_cameras: { [currentCameraId || '']: videoPath }
      }
    });
    setLabelLoader(true);
    setLabelPop(false);
    setSelectedLableSource('');
  }, [
    mutateAsync,
    currentConfigId,
    assistSourceData,
    selectedLableSource,
    allVideos,
    currentCameraId,
    videoPath,
    setLabelLoader
  ]);

  const addActivityClicked = useCallback(() => {
    let count = 0;
    for (let i = 0; i < allVideo.length; i++) {
      if (allVideo[i]?.lowres_url) {
        count += 1;
      }
    }

    count === allVideo.length
      ? isCurrentVideoEnabled && !loadingVideo && !showText && onClickAdd()
      : snack({
          message:
            'Activity can not be added till all the videos are processed!',
          severity: 'warning'
        });
  }, [
    allVideo,
    isCurrentVideoEnabled,
    loadingVideo,
    showText,
    onClickAdd,
    snack
  ]);

  // Effects
  useEffect(() => {
    if (!timelineState || !timelineState.current) return;

    const engine = timelineState.current;

    if (!isPlaying) {
      engine.listener.on('afterSetTime', ({ time }: any) => {
        if (videoRef.current) videoRef.current.currentTime = time;
      });
    }

    return () => {
      if (!engine) return;
      engine.pause();
      engine.listener.offAll();
    };
  }, [isPlaying, timelineState, videoRef]);

  useEffect(() => {
    editorData.length &&
      editorData.map((data: any) => {
        data.clips.map((clips: any) => {
          dispatch(
            deleteClip({
              activityId: data.id,
              clipId: clips.id
            })
          );
        });
      });
    labelAssisData?.map((data: any) => {
      data.clips.map((clips: any) => {
        const clipData = {
          clip_id: uuidv4(),
          cameraId: currentCameraId as string,
          start_time: convertDurationToSeconds(clips.start_time),
          end_time: convertDurationToSeconds(clips.end_time),
          selectedActivity: data.id,
          videoUrl: currentCamera?.video_url || '',
          activityName: data.activity,
          max_duration: videoRef.current?.duration || undefined
        };
        dispatch(addClip(clipData));
      });
    });
  }, [labelAssisData]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (timelineState.current && event.code === 'Space') {
        event.preventDefault();
        if (isPlaying) {
          videoRef.current?.pause();
          setIsPlaying(false);
          timelineState.current.pause();
        } else {
          videoRef.current?.play();
          setIsPlaying(true);
          timelineState.current.play({ autoEnd: true });
        }
      }
      if (
        event.ctrlKey &&
        event.key === 'x' &&
        !getCropState?.start
          .toString()
          .includes(videoRef.current?.currentTime.toString())
      ) {
        event.preventDefault();
        return !disableCrop && !loadingVideo && !showText && onClickCrop();
      }
      if (event.ctrlKey && event.key === 'a') {
        event.preventDefault();
        return !loadingVideo && !showText && onClickAdd();
      }
      if (event.ctrlKey && event.key === 'z') {
        event.preventDefault();
        if (activityDataPointer >= 1) {
          setActivityDataPointer(activityDataPointer - 1);
        }
      } else if (event.ctrlKey && event.key === 'y') {
        event.preventDefault();
        if (activityDataPointer < activityData.length - 1) {
          setActivityDataPointer(activityDataPointer + 1);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    activityDataPointer,
    activityData,
    isPlaying,
    timelineState,
    videoRef,
    getCropState,
    disableCrop,
    loadingVideo,
    showText,
    onClickCrop,
    onClickAdd,
    setActivityDataPointer
  ]);
  return (
    <>
      <Grid
        container
        gap={1}
        style={{
          borderBottom: '1px solid #e0e0e0'
        }}
        m={1}
      >
        <Grid item xs={2}>
          <Stack
            spacing={1}
            direction="row"
            pl={2}
            height={'100%'}
            alignItems={'center'}
            alignContent={'center'}
          >
            {/* {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Label Assist" placement="top-start">
                  <AutoFixHighIcon
                    style={{
                      height: 20,
                      cursor:
                        isCurrentVideoEnabled &&
                        transformActivities.filter(
                          (filteredData: any) =>
                            filteredData?.cameraId !== currentCameraId &&
                            videoData?.some(
                              (video: any) => video === filteredData?.cameraId
                            )
                        ).length
                          ? 'pointer'
                          : 'not-allowed',
                      fill:
                        isCurrentVideoEnabled &&
                        transformActivities.filter(
                          (filteredData: any) =>
                            filteredData?.cameraId !== currentCameraId &&
                            videoData?.some(
                              (video: any) => video === filteredData?.cameraId
                            )
                        ).length
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={() => {
                      isCurrentVideoEnabled &&
                        checkAllActivities('Label Assist');
                    }}
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )} */}
            <Popover
              open={labelPop}
              style={{ marginLeft: '200px', marginTop: '19%' }}
              onClose={() => {
                {
                  setLabelPop(false);
                  setSelectedLableSource('');
                }
              }}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <Typography sx={{ p: 2 }} style={{ fontWeight: 'bold' }}>
                Select To Apply Label Assist
              </Typography>
              {/* Multiselect inside a div */}
              {transformActivities.filter(
                (filteredData: any) =>
                  filteredData?.cameraId !== currentCameraId &&
                  videoData?.some(
                    (video: any) => video === filteredData?.cameraId
                  )
              ).length ? (
                <div style={{ marginLeft: '12%' }}>Source Video</div>
              ) : (
                ''
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '20px',
                  overflowY: 'scroll',
                  height: '100px'
                }}
              >
                <div style={{ padding: '10px', minWidth: '200px' }}>
                  {transformActivities.filter(
                    (filteredData: any) =>
                      filteredData?.cameraId !== currentCameraId &&
                      videoData?.some(
                        (video: any) => video === filteredData?.cameraId
                      )
                  ).length
                    ? transformActivities
                        .filter(
                          (filteredData: any) =>
                            filteredData?.cameraId !== currentCameraId &&
                            videoData?.some(
                              (video: any) => video === filteredData?.cameraId
                            )
                        )
                        .map(
                          (data: any, index: number) =>
                            data?.clips?.length > 0 && (
                              <div
                                key={index}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontWeight: 'bold'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  value={data}
                                  id={data?.cameraId}
                                  checked={
                                    selectedLableSource === data?.cameraId
                                  }
                                  onChange={() =>
                                    setSelectedLableSource(data?.cameraId)
                                  }
                                />
                                <label
                                  htmlFor={data?.cameraId}
                                  style={{
                                    marginLeft: '8px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {allVideos
                                    .filter(
                                      (video: any) =>
                                        video?.id === data?.cameraId
                                    )
                                    .map((video: any) =>
                                      video?.name?.length > 15 ? (
                                        <Tooltip title={video?.name}>
                                          <div>
                                            {video.name.substring(0, 15)}...
                                          </div>
                                        </Tooltip>
                                      ) : (
                                        video.name
                                      )
                                    )}
                                </label>
                              </div>
                            )
                        )
                    : 'Please annotate one more video to use this feature'}
                </div>
              </div>
              <div
                style={{
                  padding: '15px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Button
                  style={{ height: '25px', width: '30px', margin: '10px' }}
                  variant="outlined"
                  color="primary"
                  version="light"
                  size="small"
                  children={'Cancel'}
                  onClick={() => {
                    setLabelPop(false);
                    setSelectedLableSource('');
                  }}
                />
                <Button
                  style={{ height: '25px', width: '30px', margin: '10px' }}
                  variant="outlined"
                  color="primary"
                  version="dark"
                  size="small"
                  children={'Apply'}
                  disabled={
                    !isCurrentVideoEnabled ||
                    !selectedLableSource ||
                    !activity?.length
                  }
                  onClick={() => callLabelAssist()}
                />
              </div>
            </Popover>

            <Popover
              // id={id}
              open={openPop}
              // anchorEl={anchorEl}
              style={{ marginLeft: '200px', marginTop: '17%' }}
              onClose={() => {
                setPopOpen(false);
                setSourceCameraId('');
                setSelectedItems([]);
              }}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
            >
              <Typography sx={{ p: 2 }} style={{ fontWeight: 'bold' }}>
                Select To Apply
              </Typography>

              {/* Multiselect inside a div */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  margin: '0% 20% 0% 8%'
                }}
              >
                <div>Source Video</div>
                <div>Destination Video</div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '20px'
                }}
              >
                <div
                  style={{
                    padding: '10px',
                    minWidth: '200px',
                    overflowY: 'scroll',
                    height: '100px'
                  }}
                >
                  {transformActivities
                    .filter(
                      (filteredData: any) =>
                        videoData?.some(
                          (video: any) => video === filteredData?.cameraId
                        ),
                      videoData
                    )
                    .map(
                      (data: any, index: number) =>
                        data?.clips?.length > 0 && (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              fontWeight: 'bold'
                            }}
                          >
                            <input
                              type="checkbox"
                              value={data}
                              id={data?.cameraId}
                              checked={sourceCameraId === data?.cameraId}
                              onChange={() => onSourceChange(data)}
                            />
                            <label
                              htmlFor={data?.cameraId}
                              style={{
                                marginLeft: '8px',
                                fontWeight: 'bold'
                              }}
                            >
                              {allVideos.map((video: any) =>
                                video.id === data?.cameraId ? (
                                  video.name.length > 15 ? (
                                    <Tooltip title={video.name}>
                                      <div>
                                        {video.name.substring(0, 15)}...
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    video.name
                                  )
                                ) : null
                              )}
                            </label>
                          </div>
                        )
                    )}
                </div>
                <div
                  style={{
                    padding: '10px',
                    minWidth: '200px',
                    overflowY: 'scroll',
                    height: '100px'
                  }}
                >
                  {allVideo
                    .filter((data: any) => videoData.includes(data?.id))
                    .map((option: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontWeight: 'bold'
                        }}
                      >
                        <input
                          type="checkbox"
                          value={option}
                          id={option?.id}
                          checked={selectedItems.includes(option?.id)}
                          onChange={e => {
                            option.id !== sourceCameraId &&
                              sourceDuration &&
                              sourceDuration <=
                                option?.video_metadata?.duration &&
                              handleCheckboxChange(e, option?.id);
                          }}
                        />
                        <label
                          htmlFor={option?.id}
                          style={{
                            marginLeft: '8px',
                            fontWeight: 'bold',
                            color:
                              option.id === sourceCameraId ||
                              (sourceDuration &&
                                option?.video_metadata?.duration <
                                  sourceDuration)
                                ? 'grey'
                                : 'black'
                          }}
                        >
                          {option?.name.length > 15 ? (
                            <Tooltip title={option?.name}>
                              <div> {option?.name.substring(0, 15)}...</div>
                            </Tooltip>
                          ) : (
                            option.name
                          )}
                        </label>
                      </div>
                    ))}
                </div>
              </div>
              <div
                style={{
                  padding: '0px 20px 0px 20px',
                  fontWeight: 'bold',
                  fontSize: '10px',
                  width: '80%'
                }}
              >
                Please note that the destination videos with shorter duration
                than the source video will be disabled.
              </div>
              <div
                style={{
                  padding: '15px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Button
                  style={{ height: '25px', width: '30px', margin: '10px' }}
                  variant="outlined"
                  color="primary"
                  version="light"
                  size="small"
                  children={'Cancel'}
                  onClick={() => {
                    setPopOpen(false);
                    setSourceCameraId('');
                    setSelectedItems([]);
                  }}
                />
                <Button
                  style={{ height: '25px', width: '30px', margin: '10px' }}
                  variant="outlined"
                  color="primary"
                  version="dark"
                  size="small"
                  disabled={!sourceCameraId || !selectedItems.length}
                  children={'Apply'}
                  onClick={() => {
                    sourceCameraId &&
                      dispatch(
                        applyToAllVideos({
                          currentCameraId: sourceCameraId,
                          allCameraIds: selectedItems
                        })
                      );
                    setPopOpen(false);
                    snack({
                      message: 'Changes saved successfully!',
                      severity: 'success'
                    });
                    setSourceCameraId('');
                    setSelectedItems([]);
                  }}
                />
              </div>
            </Popover>
            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Apply on Selected" placement="top-start">
                  <img
                    src={applyToAll}
                    style={{
                      height: 18,
                      cursor: 'pointer',
                      fill: isCurrentVideoEnabled ? 'black' : 'grey'
                    }}
                    onClick={() => {
                      isCurrentVideoEnabled &&
                        checkAllActivities('Apply to All');
                    }}
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}
            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Add Activity" placement="top-start">
                  <AddIcon
                    style={{
                      height: 20,
                      cursor: 'pointer',
                      fill:
                        isCurrentVideoEnabled && !loadingVideo && !showText
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={addActivityClicked}
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}
          </Stack>
        </Grid>
        <Grid
          item
          xs={2}
          style={{
            borderLeft: '1px solid #e0e0e0',
            marginLeft: '-6%'
          }}
        >
          <Stack
            spacing={1}
            direction="row"
            pl={2}
            height={'100%'}
            alignItems={'center'}
            alignContent={'center'}
          >
            {/* HERE */}
            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Undo" placement="top-start">
                  <UndoSharpIcon
                    style={{
                      height: 20,
                      cursor:
                        isCurrentVideoEnabled &&
                        activityDataPointer >= 1 &&
                        !loadingVideo &&
                        !showText
                          ? 'pointer'
                          : 'not-allowed',
                      fill:
                        isCurrentVideoEnabled &&
                        activityDataPointer >= 1 &&
                        !loadingVideo &&
                        !showText
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={() =>
                      isCurrentVideoEnabled &&
                      !loadingVideo &&
                      !showText &&
                      activityDataPointer >= 1 &&
                      setActivityDataPointer(activityDataPointer - 1)
                    }
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}
            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Redo" placement="top-start">
                  <RedoSharpIcon
                    style={{
                      height: 20,
                      cursor:
                        isCurrentVideoEnabled &&
                        activityDataPointer < activityData.length - 1 &&
                        !loadingVideo &&
                        !showText
                          ? 'pointer'
                          : 'not-allowed',
                      fill:
                        isCurrentVideoEnabled &&
                        activityDataPointer < activityData.length - 1 &&
                        !loadingVideo &&
                        !showText
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={() =>
                      isCurrentVideoEnabled &&
                      !loadingVideo &&
                      !showText &&
                      activityDataPointer < activityData.length - 1 &&
                      setActivityDataPointer(activityDataPointer + 1)
                    }
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}
            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Cut Activity" placement="top-start">
                  <ContentCutIcon
                    style={{
                      height: 20,
                      cursor:
                        isCurrentVideoEnabled &&
                        !disableCrop &&
                        videoRef?.current?.currentTime &&
                        getCropState?.start <= videoRef?.current?.currentTime &&
                        !loadingVideo &&
                        !showText &&
                        !getCropState?.start
                          .toString()
                          .includes(videoRef.current?.currentTime.toString())
                          ? 'pointer'
                          : 'not-allowed',
                      fill:
                        isCurrentVideoEnabled &&
                        !getCropState?.start
                          .toString()
                          .includes(videoRef.current?.currentTime.toString()) &&
                        videoRef?.current?.currentTime &&
                        getCropState?.start <= videoRef?.current?.currentTime &&
                        !disableCrop &&
                        !loadingVideo &&
                        !showText
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={
                      isCurrentVideoEnabled &&
                      !getCropState?.start
                        .toString()
                        .includes(videoRef.current?.currentTime.toString()) &&
                      !disableCrop &&
                      videoRef?.current?.currentTime &&
                      getCropState?.start <= videoRef?.current?.currentTime &&
                      !loadingVideo &&
                      !showText
                        ? onClickCrop
                        : () => {}
                    }
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}
          </Stack>
        </Grid>
        <Grid item xs={7.5} style={{ marginLeft: '8%' }}>
          <Stack
            spacing={1}
            direction="row"
            pl={2}
            height={'100%'}
            alignItems={'center'}
            alignContent={'center'}
            justifyContent={'flex-end'}
          >
            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Zoom out" placement="top-start">
                  <ZoomOutIcon
                    style={{
                      height: 20,
                      cursor:
                        isCurrentVideoEnabled &&
                        zoomCount > 0 &&
                        !loadingVideo &&
                        !showText
                          ? 'pointer'
                          : 'not-allowed',
                      fill:
                        isCurrentVideoEnabled &&
                        zoomCount > 0 &&
                        !loadingVideo &&
                        !showText
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={() =>
                      isCurrentVideoEnabled &&
                      !loadingVideo &&
                      !showText &&
                      zoomCount > 0 &&
                      setZoomCOunt(zoomCount - 1)
                    }
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}

            {scale > 0 ? (
              <div className="play-control">
                <Tooltip title="Zoom in" placement="top-start">
                  <ZoomInIcon
                    style={{
                      height: 20,
                      cursor:
                        isCurrentVideoEnabled &&
                        zoomCount < 2 &&
                        !loadingVideo &&
                        !showText
                          ? 'pointer'
                          : 'not-allowed',
                      fill:
                        isCurrentVideoEnabled &&
                        zoomCount < 2 &&
                        !loadingVideo &&
                        !showText
                          ? 'black'
                          : 'grey'
                    }}
                    onClick={() =>
                      isCurrentVideoEnabled &&
                      !loadingVideo &&
                      !showText &&
                      zoomCount < 2 &&
                      setZoomCOunt(zoomCount + 1)
                    }
                  />
                </Tooltip>
              </div>
            ) : (
              <Skeleton variant="rectangular" height={20} width={30} />
            )}
          </Stack>
        </Grid>
        <Modal
          open={isOpen.open}
          onClose={() => setOpen({ open: false, value: '' })}
          size="md"
        >
          <div style={{ fontWeight: 'bold' }}>{isOpen.value}</div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '10px 0px 15px 0px'
            }}
          >
            To enable {isOpen.value} you have to annotate manually the
            activities atleast for one video. Once that's done, it will appear
            here to select.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button onClick={() => setOpen({ open: false, value: '' })}>
              OK
            </Button>
          </div>
        </Modal>
        <Snackbar
          open={openMessage}
          autoHideDuration={6000}
          onClose={() => setOpenmessage(false)}
          message="Please turn on auto save to make sure you don't loose your changes."
        />
      </Grid>
    </>
  );
}

export default memo(TimelinePlayer);
